import { CLIENT_STATUS_GROUPS, normalizeClientStatus } from './statusConfig';

const DONE_STATUSES = CLIENT_STATUS_GROUPS.completed;

export function needsUpload(job) {
  return normalizeClientStatus(job.status) === 'order_received' && job.fileName == null;
}

/** The single next step a client can take on a job, shown as the button in the Recent Jobs table. */
export function nextAction(job) {
  const status = normalizeClientStatus(job.status);
  const to = `/jobs/${job.jobId}`;
  if (status === 'ready_for_review') return { label: 'Review iXBRL', to, primary: true, attention: true };
  if (status === 'payment_pending' || status === 'awaiting_payment') return { label: 'Complete Payment', to, primary: true, attention: true };
  if (needsUpload(job)) return { label: 'Upload Documents', to, primary: false, attention: true };
  if (DONE_STATUSES.includes(status)) return { label: 'Download Files', to, primary: false, attention: false };
  return { label: 'View Progress', to, primary: false, attention: false };
}

export function isActionRequired(job) {
  return nextAction(job).attention;
}

export function summariseJobs(jobs) {
  const actionRequired = jobs.filter(isActionRequired).length;
  const completed = jobs.filter((j) => DONE_STATUSES.includes(normalizeClientStatus(j.status))).length;
  const inProgress = jobs.filter((j) => !isActionRequired(j) && !DONE_STATUSES.includes(normalizeClientStatus(j.status))).length;
  return { total: jobs.length, inProgress, actionRequired, completed };
}

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/** Ordered "what would you like to do next" rows; only rows that apply are returned, creating a job is always last. */
export function buildNextSteps({ jobs, pendingInvoices }) {
  const byStatus = (status) => jobs.filter((j) => normalizeClientStatus(j.status) === status);
  const review = byStatus('ready_for_review');
  const payment = jobs.filter((j) => ['payment_pending', 'awaiting_payment'].includes(normalizeClientStatus(j.status)));
  const upload = jobs.filter(needsUpload);
  const steps = [];
  if (review.length) {
    steps.push({ id: 'review', title: 'Review your deliverables', detail: `${plural(review.length, 'job is', 'jobs are')} ready for your review`, cta: 'Review', to: review.length === 1 ? `/jobs/${review[0].jobId}` : '/jobs?status=review' });
  }
  if (payment.length) {
    steps.push({ id: 'payment', title: 'Complete payment', detail: `${plural(payment.length, 'order is', 'orders are')} waiting for payment`, cta: 'Pay now', to: payment.length === 1 ? `/jobs/${payment[0].jobId}` : '/jobs' });
  }
  if (upload.length) {
    steps.push({ id: 'upload', title: 'Upload your documents', detail: `${plural(upload.length, 'job is', 'jobs are')} waiting for files`, cta: 'Upload', to: upload.length === 1 ? `/jobs/${upload[0].jobId}` : '/jobs' });
  }
  if (pendingInvoices.length) {
    steps.push({ id: 'invoices', title: 'Pay outstanding invoices', detail: `${plural(pendingInvoices.length, 'invoice is', 'invoices are')} unpaid`, cta: 'View invoices', to: '/invoices' });
  }
  steps.push({ id: 'create', title: 'Create a new filing job', detail: 'Choose a service, upload your documents, review and pay', cta: 'Get started', to: '/wizard/start' });
  return steps;
}

/** First-use checklist with a progress count. */
export function buildChecklist({ companies, jobs }) {
  const items = [
    { id: 'company', label: 'Add a company', done: companies.length > 0, to: '/companies?add=1' },
    { id: 'job', label: 'Create your first job', done: jobs.length > 0, to: '/wizard/start' },
    { id: 'upload', label: 'Upload your documents', done: jobs.some((j) => j.fileName != null), to: jobs.length ? '/jobs' : '/wizard/start' },
    { id: 'payment', label: 'Complete a payment', done: jobs.some((j) => !['payment_pending', 'awaiting_payment'].includes(normalizeClientStatus(j.status))), to: jobs.length ? '/jobs' : '/wizard/start' },
  ];
  return { items, completed: items.filter((i) => i.done).length };
}
